import type { i18n as I18nType } from "i18next";
import type { NotificationCopyDescriptor } from "./types";

const INTERPOLATION_TOKEN_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*\.[A-Za-z0-9_.-]+$/;
const TRANSLATABLE_ENUM_PREFIXES = new Set(["types", "type", "status"]);
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[Tt].*)?(?:Z|[+-]\d{2}:\d{2})?$/;

const parseIsoDate = (input: string): Date | null => {
  if (!ISO_DATE_PATTERN.test(input)) {
    return null;
  }

  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasTranslationKey = (
  i18n: I18nType,
  key: string,
  options?: Record<string, unknown>,
) => {
  const exists = i18n.exists as (
    translationKey: string,
    translationOptions?: Record<string, unknown>,
  ) => boolean;

  return exists(key, options);
};

const translateDynamicKey = (
  i18n: I18nType,
  key: string,
  options?: Record<string, unknown>,
) => {
  const translate = i18n.t as (
    translationKey: string,
    translationOptions?: Record<string, unknown>,
  ) => string;

  return translate(key, options);
};

const toTitleCase = (input: string) =>
  input
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

const humanizeEnumValue = (value: string) => {
  const [, rawToken = value] = value.split(/\.(.+)/);
  const lastSegment = rawToken.split(".").pop() ?? rawToken;
  const normalized = lastSegment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[\s_-]+/g, " ")
    .trim()
    .toLowerCase();

  if (!normalized) {
    return value;
  }

  return toTitleCase(normalized);
};

const buildInterpolationCandidates = (token: string) => {
  const [rawPrefix, rawValue = ""] = token.split(/\.(.+)/);
  const prefix = rawPrefix.toLowerCase();
  const canonicalPrefix = prefix === "type" ? "types" : prefix;
  const valueUpper = rawValue.toUpperCase();
  const valueLower = rawValue.toLowerCase();

  if (!TRANSLATABLE_ENUM_PREFIXES.has(prefix)) {
    return [token];
  }

  const candidates = new Set<string>([
    token,
    `${canonicalPrefix}.${rawValue}`,
    `${canonicalPrefix}.${valueUpper}`,
    `${canonicalPrefix}.${valueLower}`,
    `notifications:${canonicalPrefix}.${rawValue}`,
    `notifications:${canonicalPrefix}.${valueUpper}`,
    `notifications:${canonicalPrefix}.${valueLower}`,
    `common:${canonicalPrefix}.${rawValue}`,
    `common:${canonicalPrefix}.${valueUpper}`,
    `common:${canonicalPrefix}.${valueLower}`,
    `notifications:notifications.${canonicalPrefix}.${rawValue}`,
    `notifications:notifications.${canonicalPrefix}.${valueUpper}`,
    `notifications:notifications.${canonicalPrefix}.${valueLower}`,
    `common:common.${canonicalPrefix}.${rawValue}`,
    `common:common.${canonicalPrefix}.${valueUpper}`,
    `common:common.${canonicalPrefix}.${valueLower}`,
  ]);

  if (prefix === "status") {
    candidates.add(`common:common.status.${valueLower}`);
    candidates.add(`common:status.${valueLower}`);
  }

  return Array.from(candidates);
};

const translateInterpolationToken = (i18n: I18nType, value: string) => {
  if (!INTERPOLATION_TOKEN_PATTERN.test(value)) {
    return value;
  }

  const candidates = buildInterpolationCandidates(value);

  for (const candidate of candidates) {
    if (hasTranslationKey(i18n, candidate)) {
      return translateDynamicKey(i18n, candidate);
    }
  }

  return humanizeEnumValue(value);
};

const resolveInterpolationParams = (
  i18n: I18nType,
  input: unknown,
): unknown => {
  if (Array.isArray(input)) {
    return input.map((value) => resolveInterpolationParams(i18n, value));
  }

  if (input && typeof input === "object") {
    const entries = Object.entries(input as Record<string, unknown>).map(
      ([key, value]) =>
        [key, resolveInterpolationParams(i18n, value)] as const,
    );

    return Object.fromEntries(entries);
  }

  if (typeof input === "string") {
    const maybeDate = parseIsoDate(input);
    if (maybeDate) {
      return maybeDate;
    }

    return translateInterpolationToken(i18n, input);
  }

  return input;
};

const normalizeNotificationKey = (key?: string) => {
  if (!key) {
    return null;
  }

  if (key.includes(":")) {
    return key;
  }

  const normalizedKey = key.startsWith("notifications.")
    ? key.slice("notifications.".length)
    : key;

  return `notifications:${normalizedKey}`;
};

export const resolveNotificationCopy = (
  i18n: I18nType,
  descriptor?: NotificationCopyDescriptor,
  fallback = "",
) => {
  if (!descriptor) {
    return fallback;
  }

  const normalizedKey = normalizeNotificationKey(descriptor.key);
  const resolvedParams = resolveInterpolationParams(i18n, descriptor.params);
  const interpolationOptions =
    resolvedParams && typeof resolvedParams === "object"
      ? (resolvedParams as Record<string, unknown>)
      : undefined;

  if (
    normalizedKey &&
    hasTranslationKey(i18n, normalizedKey, interpolationOptions)
  ) {
    return translateDynamicKey(i18n, normalizedKey, interpolationOptions);
  }

  return descriptor.fallback ?? fallback;
};
